import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Grid,
    MenuItem,
} from '@mui/material';

function ExerciseForm() {
    const { sessionId, exerciseId } = useParams();
    const navigate = useNavigate();
    const [exercise, setExercise] = useState({
        name: '',
        text_moteur: '',
        text_cognitif: '',
        vocal_moteur: '',
        vocal_moteur_mp3: '',
        vocal_cognitif: '',
        vocal_cognitif_mp3: '',
        explication_moteur: '',
        explication_cognitif: '',
        duration: 120,
        category: 'Équilibre',
        thumbnail: '',
        thumbnail_cognitif: '',
        materiel: [],
        type: '',
        timelines: []
    });
    const isEdit = Boolean(exerciseId);

    useEffect(() => {
        if (isEdit) {
            const fetchExercise = async () => {
                try {
                    const response = await apiClient.get(`/sessions/${sessionId}`);
                    const found = response.data?.exercises?.find(e => String(e.id) === String(exerciseId));
                    if (found) {
                        // Normaliser le matériel pour s'assurer que c'est toujours un tableau
                        const normalizedExercise = {
                            ...found,
                            materiel: Array.isArray(found.materiel) 
                                ? found.materiel 
                                : (found.materiel ? [found.materiel] : []),
                            type: found.type || ''
                        };
                        setExercise(normalizedExercise);
                    }
                } catch (error) {
                    console.error('Erreur lors de la récupération de l\'exercice:', error);
                }
            };
            fetchExercise();
        }
    }, [isEdit, sessionId, exerciseId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setExercise(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) {
                await apiClient.put(`/sessions/${sessionId}/exercises/${exerciseId}`, exercise);
            } else {
                await apiClient.post(`/sessions/${sessionId}/exercises`, exercise);
            }
            navigate(`/sessions/${sessionId}`);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de l\'exercice:', error);
        }
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {isEdit ? 'Modifier l\'exercice' : 'Nouvel exercice'}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Titre-Court-Ecrit"
                            name="name"
                            value={exercise.name}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Intro-Ecrite-moteur (avant l'exercice)"
                            name="text_moteur"
                            value={exercise.text_moteur}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Explication-Vocale-Moteur (avant l'exercice)"
                            name="vocal_moteur"
                            value={exercise.vocal_moteur}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            helperText="Texte qui sera lu pour la partie motrice (TTS)"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Fichier MP3 vocal motrice (URL)"
                            name="vocal_moteur_mp3"
                            value={exercise.vocal_moteur_mp3 || ''}
                            onChange={handleChange}
                            helperText="URL du fichier MP3 à lire pour la partie motrice (prioritaire sur le TTS)"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Explication-écrite-moteure (dans l'exercice, par défaut)"
                            name="explication_moteur"
                            value={exercise.explication_moteur}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            helperText="Texte qui sera afficher pour la partie motrice"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Intro-Ecrite-cognitif (avant l'exercice)"
                            name="text_cognitif"
                            value={exercise.text_cognitif}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Intro-Vocale-Cognitif (avant l'exercice)"
                            name="vocal_cognitif"
                            value={exercise.vocal_cognitif}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            helperText="Texte qui sera lu pour la partie cognitive (TTS)"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Fichier MP3 vocal cognitive (URL)"
                            name="vocal_cognitif_mp3"
                            value={exercise.vocal_cognitif_mp3 || ''}
                            onChange={handleChange}
                            helperText="URL du fichier MP3 à lire pour la partie cognitive (prioritaire sur le TTS)"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Explication-écrite (dans l'exercice, par défaut)"
                            name="explication_cognitif"
                            value={exercise.explication_cognitif}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            helperText="Texte qui sera afficher pour la partie cognitive"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Thumbnail (URL)"
                            name="thumbnail"
                            value={exercise.thumbnail}
                            onChange={handleChange}
                            helperText="URL de l'image miniature de l'exercice"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Thumbnail Cognitif (Nom du fichier)"
                            name="thumbnail_cognitif"
                            value={exercise.thumbnail_cognitif || ''}
                            onChange={handleChange}
                            helperText="URL de l'image miniature cognitive de l'exercice"
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            select
                            label="Type"
                            name="type"
                            value={exercise.type || ''}
                            onChange={handleChange}
                            helperText="Sélectionnez le type d'exercice"
                        >
                            <MenuItem value="echauffement">Echauffement</MenuItem>
                            <MenuItem value="exercice">Exercice</MenuItem>
                            <MenuItem value="etirement">Etirement</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            select
                            label="Matériel nécessaire"
                            name="materiel"
                            value={exercise.materiel || []}
                            onChange={(e) => {
                                const value = e.target.value;
                                const materielValue = typeof value === 'string' ? value.split(',') : value;
                                setExercise(prev => ({ ...prev, materiel: materielValue }));
                            }}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => {
                                    if (!selected || selected.length === 0) {
                                        return '';
                                    }
                                    return Array.isArray(selected) ? selected.join(', ') : selected;
                                }
                            }}
                            helperText="Sélectionnez un ou plusieurs matériels"
                        >
                            <MenuItem value="Chaise">Une chaise</MenuItem>
                            <MenuItem value="Tapis">Un tapis</MenuItem>
                            <MenuItem value="Poids">Des poids</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Durée (secondes)"
                            name="duration"
                            type="number"
                            value={exercise.duration}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={6}>
                        <TextField
                            fullWidth
                            select
                            label="Catégorie"
                            name="category"
                            value={exercise.category}
                            onChange={handleChange}
                            required
                        >
                            <MenuItem value="Echauffement">Echauffement</MenuItem>
                            <MenuItem value="Équilibre">Équilibre</MenuItem>
                            <MenuItem value="Coordination">Coordination</MenuItem>
                            <MenuItem value="Force">Force</MenuItem>
                            <MenuItem value="Flexibilité">Flexibilité</MenuItem>
                            <MenuItem value="Etirements">Etirements</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} style={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/sessions/${sessionId}`)}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                {isEdit ? 'Mettre à jour' : 'Ajouter l\'exercice'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
}

export default ExerciseForm; 